import {
    useContext,
    useEffect,
    useState
} from "react";
import {
    TouchableOpacity,
    Platform,
    Image,
    View
} from "react-native";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitSnackBar,
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    PageContainer,
    Text
} from "ncore-ui-kit";
import type {
    GetProfilePayloadType
} from "../../../../services/restAPI/actions/getProfile/type";
import {
    ChevronRightIcon,
    KeyRoundIcon,
    CameraIcon,
    MailIcon,
    UserIcon
} from "lucide-react-native";
import {
    SERVICE_EVENT_TYPES
} from "../../../../services/restAPI/constants";
import {
    AuthContext
} from "../../../../contexts/AuthContext";
import RESTServiceContext from "../../../../services/restAPI/context";
import ChangePasswordModal from "../changePasswordModal";
import EditProfileModal from "../editProfileModal";
import ChangeMailModal from "../changeMailModal";

const ProfileMain = () => {
    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        updateUser,
        user
    } = useContext(AuthContext);

    const [
        isChangePasswordModalVisible,
        setIsChangePasswordModalVisible
    ] = useState<boolean>(false);

    const [
        isEditProfileModalVisible,
        setIsEditProfileModalVisible
    ] = useState<boolean>(false);

    const [
        isChangeMailModalVisible,
        setIsChangeMailModalVisible
    ] = useState<boolean>(false);

    const [
        avatarPreview,
        setAvatarPreview
    ] = useState<string>("");

    const [
        isUploading,
        setIsUploading
    ] = useState<boolean>(false);

    const [
        profile,
        setProfile
    ] = useState<GetProfilePayloadType | null>(null);

    const fetchProfile = () => {
        RESTServiceContext.action("getProfile", null).then(res => {
            if (res.payload) {
                setProfile(res.payload);
                if (res.payload.profilePhoto) {
                    setAvatarPreview(res.payload.profilePhoto);
                    if (user && user.profilePhoto !== res.payload.profilePhoto) {
                        updateUser({
                            profilePicturePath: res.payload.profilePhoto,
                            profilePhoto: res.payload.profilePhoto
                        });
                    }
                }
            }
        }).catch(err => {
            console.error(err);
        });
    };

    useEffect(() => {
        fetchProfile();
    }, []);

    const uploadPhoto = async (file: File) => {
        try {
            setIsUploading(true);
            const result = await RESTServiceContext.action("getUploadTokenForProfilePhoto", null);
            const uploadResult = result as unknown as { payload?: { token: string } };

            if (uploadResult.payload?.token) {
                const formData = new FormData();
                formData.append("file", file);

                const response = await fetch(`${process.env.STORAGE_URL}/upload`, {
                    headers: {
                        Authorization: uploadResult.payload.token
                    },
                    body: formData,
                    method: "POST"
                });

                if (response.ok) {
                    const uploadData = await response.json();
                    let uploadedPhotoUrl = "";

                    if (uploadData?.payload?.key) {
                        const key = uploadData.payload.key;
                        uploadedPhotoUrl = `${process.env.STORAGE_URL}/uploads${key.startsWith("/") ? "" : "/"}${key}`;
                    }

                    if (uploadedPhotoUrl) {
                        setAvatarPreview(uploadedPhotoUrl);
                        await updateUser({
                            profilePicturePath: uploadedPhotoUrl,
                            profilePhoto: uploadedPhotoUrl
                        });
                        RESTServiceContext.emitWithKey(SERVICE_EVENT_TYPES.USER_UPDATED, {
                            profilePicturePath: uploadedPhotoUrl,
                            profilePhoto: uploadedPhotoUrl
                        });
                        setProfile(prev => {
                            if (!prev) {
                                return null;
                            }
                            return {
                                ...prev,
                                profilePhoto: uploadedPhotoUrl
                            };
                        });
                    }

                    setTimeout(async () => {
                        try {
                            const meRes = await RESTServiceContext.action("me", null);
                            if (meRes?.payload?.profilePhoto) {
                                await updateUser({
                                    profilePicturePath: meRes.payload.profilePhoto,
                                    profilePhoto: meRes.payload.profilePhoto
                                });
                                setAvatarPreview(meRes.payload.profilePhoto);
                                setProfile(prev => {
                                    if (!prev) {
                                        return null;
                                    }
                                    return {
                                        ...prev,
                                        profilePhoto: meRes.payload?.profilePhoto
                                    };
                                });
                            }
                        } catch (err) {
                            console.error(err);
                        }
                    }, 500);

                    NCoreUIKitSnackBar.open({
                        title: localize("unknown") || "Fotoğraf başarıyla güncellendi.",
                        type: "success"
                    });
                } else {
                    throw new Error("Upload failed");
                }
            }
        } catch (error) {
            console.error(error);
            NCoreUIKitSnackBar.open({
                title: localize("unknown") || "Fotoğraf yüklenirken bir hata oluştu.",
                type: "danger"
            });
        } finally {
            setIsUploading(false);
        }
    };

    const handleAvatarPick = () => {
        if (Platform.OS === "web") {
            const input = document.createElement("input");
            input.type = "file";
            input.accept = "image/*";
            input.onchange = (e: Event) => {
                const target = e.target as HTMLInputElement;
                const file = target.files?.[0];
                if (file) {
                    if (file.size > 5 * 1024 * 1024) {
                        NCoreUIKitSnackBar.open({
                            title: localize("members-invalid-file-size") || "Dosya boyutu 5 MB'dan küçük olmalıdır.",
                            type: "warning"
                        });
                        return;
                    }
                    setAvatarPreview(URL.createObjectURL(file));
                    uploadPhoto(file);
                }
            };
            input.click();
        }
    };

    const renderCard = (
        description: string,
        icon: React.ReactNode,
        onPress: () => void,
        title: string
    ) => {
        return <TouchableOpacity
            style={[
                stylesheet.cardContainer,
                {
                    backgroundColor: colors.project.brandWhite,
                    borderRadius: radiuses.md,
                    padding: spaces.spacingXl
                }
            ]}
            onPress={onPress}
        >
            <View
                style={[
                    stylesheet.cardContent,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                {icon}
                <View>
                    <Text
                        variant="titleMediumSize"
                    >
                        {title}
                    </Text>
                    <Text
                        customColor={colors.content.text.mid}
                        variant="bodyMediumSize"
                    >
                        {description}
                    </Text>
                </View>
            </View>
            <ChevronRightIcon
                color={colors.content.icon.mid}
                size={24}
            />
        </TouchableOpacity>;
    };

    const renderProfileHeader = () => {
        return <View
            style={[
                stylesheet.profileHeader,
                {
                    backgroundColor: colors.project.brandWhite,
                    borderRadius: radiuses.md,
                    padding: spaces.spacingXl,
                    gap: spaces.spacingXl
                }
            ]}
        >
            <TouchableOpacity
                style={[
                    stylesheet.avatarWrapper
                ]}
                onPress={handleAvatarPick}
                disabled={isUploading}
            >
                <View
                    style={[
                        stylesheet.avatarContainer,
                        {
                            backgroundColor: colors.content.container.subtle,
                            borderColor: colors.content.border.emphasized,
                            borderWidth: borders.seperator,
                            borderRadius: radiuses.full
                        }
                    ]}
                >
                    {
                        avatarPreview ?
                            <Image
                                style={[
                                    stylesheet.avatarImage,
                                    {
                                        borderRadius: radiuses.full
                                    }
                                ]}
                                source={{
                                    uri: avatarPreview
                                }}
                            />
                        :
                            <UserIcon
                                color={colors.content.icon.mid}
                                size={48}
                            />
                    }
                </View>
                <View
                    style={[
                        stylesheet.editIconContainer,
                        {
                            borderColor: colors.content.border.emphasized,
                            backgroundColor: colors.project.brandWhite,
                            borderWidth: borders.seperator,
                            borderRadius: radiuses.full,
                            padding: spaces.spacingXs
                        }
                    ]}
                >
                    <CameraIcon
                        color={colors.content.icon.high}
                        size={16}
                    />
                </View>
            </TouchableOpacity>
            <View
                style={[
                    stylesheet.profileInfo,
                    {
                        gap: spaces.spacingXs
                    }
                ]}
            >
                <Text
                    variant="titleLargeSize"
                >
                    {profile?.fullName || (profile?.firstName ? (profile.firstName + " " + profile.lastName) : "-")}
                </Text>
                <Text
                    customColor={colors.content.text.mid}
                    variant="bodyMediumSize"
                >
                    {profile?.mail || "-"}
                </Text>
                {
                    profile?.phoneNumber ?
                        <Text
                            customColor={colors.content.text.mid}
                            variant="bodyMediumSize"
                        >
                            {profile.phoneNumber}
                        </Text>
                    :
                        null
                }
            </View>
        </View>;
    };

    return <PageContainer
        isScrollable={true}
    >
        <View
            style={[
                stylesheet.container,
                {
                    padding: spaces.spacingXl,
                    gap: spaces.spacingXl
                }
            ]}
        >
            {renderProfileHeader()}
            {renderCard(
                localize("edit-profile-info-desc") || "Ad, soyad ve telefon bilgilerinizi güncelleyin.",
                <UserIcon
                    color={colors.content.icon.high}
                    size={24}
                />,
                () => {
                    setIsEditProfileModalVisible(true);
                },
                localize("edit-profile-info") || "Profil Bilgilerini Düzenle"
            )}
            {renderCard(
                localize("change-password-desc"),
                <KeyRoundIcon
                    color={colors.content.icon.high}
                    size={24}
                />,
                () => {
                    setIsChangePasswordModalVisible(true);
                },
                localize("change-password")
            )}
            {renderCard(
                localize("change-mail-desc") || "Hesap e-posta adresinizi güvenli bir şekilde güncelleyin.",
                <MailIcon
                    color={colors.content.icon.high}
                    size={24}
                />,
                () => {
                    setIsChangeMailModalVisible(true);
                },
                localize("change-mail") || "E-posta Değiştirme"
            )}
        </View>
        <ChangePasswordModal
            onClose={() => setIsChangePasswordModalVisible(false)}
            isVisible={isChangePasswordModalVisible}
        />
        <EditProfileModal
            onClose={() => setIsEditProfileModalVisible(false)}
            initialData={{
                phoneNumber: profile?.phoneNumber,
                firstName: profile?.firstName,
                lastName: profile?.lastName
            }}
            isVisible={isEditProfileModalVisible}
            onSuccess={fetchProfile}
        />
        <ChangeMailModal
            onClose={() => setIsChangeMailModalVisible(false)}
            isVisible={isChangeMailModalVisible}
            currentMail={profile?.mail}
            onSuccess={fetchProfile}
        />
    </PageContainer>;
};

export default ProfileMain;
